// front-end/src/components/block-system/block-manager/block-api.js

import { config, getBlockApiUrl } from '../../../config/app-config';
import { apiGet, apiPost, apiDelete } from '../../../utils/api';
import { showErrorNotification, showSuccessNotification } from '../../../utils/notifications';
import { getAuthToken, isAuthenticated, verifyAuth } from '../../auth';
import { NewsManager } from '../../news-manager/news-manager';

// Утилита для проверки, является ли блок новостным
export function isNewsBlock(blockElement) {
  if (!blockElement) return false;

  const blockName = blockElement.getAttribute('data-block-name');
  if (!blockName) return false;

  // Проверяем все возможные варианты новостных блоков
  return blockName.startsWith('news-') ||
         blockName.startsWith('news-block-') ||
         blockElement.classList.contains('news-block');
}

// НОВОЕ: Утилита для проверки, является ли блок кейсом
export function isCaseBlock(blockElement) {
  if (!blockElement) return false;

  const blockName = blockElement.getAttribute('data-block-name');
  if (!blockName) return false;

  // Проверяем что блок является кейсом
  return blockName.startsWith('case-card-') ||
         blockElement.classList.contains('case-slide');
}

export async function fetchBlocksBySection(sectionName) {
  try {
    const blocks = await apiGet(`${config.api.paths.blocksByParent}/${encodeURIComponent(sectionName)}`, { auth: false });

    // Фильтруем блоки вакансий для неавторизованных
    if (sectionName === 'vacancies-section' && !isAuthenticated()) {
      // ... существующий код фильтрации вакансий ...
    }

    // НОВОЕ: Фильтруем блоки курсов для неавторизованных
    if (sectionName === 'courses-section' && !isAuthenticated()) {
      console.log(`[BlockAPI] Фильтруем блоки курсов для неавторизованного пользователя`);

      // Загружаем информацию о курсах для каждого блока
      const blocksWithCourseInfo = await Promise.all(blocks.map(async (block) => {
        if (block.data_block_name && block.data_block_name.startsWith('course-card-') && block.id) {
          try {
            // Пытаемся получить информацию о курсе
            const courseData = await apiGet(`${config.api.paths.coursesByBlockId}/${block.id}`, {auth: false});
            if (courseData) {
              // Добавляем статус курса к блоку
              block.course_status = courseData.status;
              block.course_id = courseData.id;
              block.course_slug = courseData.slug;
            }
          } catch (error) {
            console.warn(`[BlockAPI] Не удалось загрузить данные курса для блока ${block.data_block_name}`);
            // Если не удалось загрузить, считаем что блок в draft
            block.course_status = 'draft';
          }
        }
        return block;
      }));

      // Фильтруем только опубликованные курсы
      const filteredBlocks = blocksWithCourseInfo.filter(block => {
        if (!block.data_block_name || !block.data_block_name.startsWith('course-card-')) {
          return true; // Не курс - показываем
        }
        return block.course_status === 'published';
      });

      console.log(`[BlockAPI] Отфильтровано ${blocks.length - filteredBlocks.length} неопубликованных курсов`);
      return filteredBlocks;
    }

    return blocks;
  } catch (error) {
    console.error(`[BlockAPI] Error fetching blocks for section ${sectionName}:`, error);
    return [];
  }
}

// НОВАЯ ФУНКЦИЯ: Загрузка блоков по странице
export async function fetchBlocksByPage(pageTitle) {
  return apiGet(`${config.api.paths.blocksByPage}/${encodeURIComponent(pageTitle)}`, { auth: false })
    .then(data => data || [])
    .catch(() => []);
}

export async function createBlock(blockData) {
  return apiPost(config.api.paths.blocks, blockData);
}

// В файле front-end/src/components/block-system/block-manager/block-api.js
// Добавить после функции isCaseBlock:

// НОВОЕ: Утилита для проверки, является ли блок вакансией
export function isVacancyBlock(blockElement) {
  if (!blockElement) return false;

  const blockName = blockElement.getAttribute('data-block-name');
  if (!blockName) return false;

  // Проверяем что блок является вакансией
  return blockName.startsWith('vacancy-') ||
         blockElement.classList.contains('vacancy-slide');
}

export function isCourseBlock(blockElement) {
  if (!blockElement) return false;

  const blockName = blockElement.getAttribute('data-block-name');
  if (!blockName) return false;

  return blockName.startsWith('course-card-') ||
         blockElement.classList.contains('course-card');
}

export function isDiagnosticBlock(blockElement) {
  if (!blockElement) return false;

  const blockName = blockElement.getAttribute('data-block-name');
  if (!blockName) return false;

  return blockName.startsWith('diagnostic-card-') ||
         blockElement.classList.contains('diagnostic-card');
}

// Обновляем функцию deleteBlockById:

export async function deleteBlockById(blockId, blockElement = null) {
  console.log(`Starting deletion process for block ID: ${blockId}`);

  // Check if this is a special block that needs custom deletion
  if (blockElement) {
    const blockName = blockElement.getAttribute('data-block-name');

    console.log(`Block details: name=${blockName}`);

    // Проверяем новости
    if (isNewsBlock(blockElement)) {
      console.log(`This is a news block - checking for news entity`);
      const newsId = blockElement.getAttribute('data-news-id');

      if (newsId) {
        console.log(`News entity found (ID: ${newsId}) - using news deletion endpoint`);

        try {
          const result = await apiDelete(`${config.api.paths.news}/${newsId}`);
          console.log('✅ News entity deleted successfully with cascade');
          return result;
        } catch (error) {
          console.error('❌ Error deleting via news endpoint:', error);
          console.log('⚠️  Falling back to regular block deletion');
        }
      }
    }

    // Проверяем кейсы
    if (isCaseBlock(blockElement)) {
      console.log(`This is a case block - checking for case entity`);
      const newsId = blockElement.getAttribute('data-news-id');

      if (newsId) {
        console.log(`Case entity found (ID: ${newsId}) - using news deletion endpoint for case`);

        try {
          const result = await apiDelete(`${config.api.paths.news}/${newsId}`);
          console.log('✅ Case entity deleted successfully with cascade');
          return result;
        } catch (error) {
          console.error('❌ Error deleting case via news endpoint:', error);
          console.log('⚠️  Falling back to regular block deletion');
        }
      }
    }

    // Проверяем вакансии
    if (isVacancyBlock(blockElement)) {
      console.log(`This is a vacancy block - checking for vacancy entity`);
      const vacancyId = blockElement.getAttribute('data-vacancy-id');

      if (vacancyId) {
        console.log(`Vacancy entity found (ID: ${vacancyId}) - using vacancy deletion endpoint`);

        try {
          const result = await apiDelete(`${config.api.paths.vacancies}/${vacancyId}`);
          console.log('✅ Vacancy entity deleted successfully with cascade');
          return result;
        } catch (error) {
          console.error('❌ Error deleting vacancy via vacancy endpoint:', error);
          console.log('⚠️  Falling back to regular block deletion');
        }
      }
    }

    // НОВОЕ: Проверяем курсы
    if (isCourseBlock(blockElement)) {
      console.log(`This is a course block - checking for course entity`);
      const courseId = blockElement.getAttribute('data-course-id');

      if (courseId) {
        console.log(`Course entity found (ID: ${courseId}) - using course deletion endpoint`);

        try {
          const result = await apiDelete(`${config.api.paths.courses}/${courseId}`);
          console.log('✅ Course entity deleted successfully with cascade (course, page, block, content)');
          return result;
        } catch (error) {
          console.error('❌ Error deleting course via course endpoint:', error);
          console.log('⚠️  Falling back to regular block deletion');
        }
      } else {
        console.log(`This is a course block but without course entity - using regular block deletion`);
      }
    }

    // Diagnostic blocks: delete only the block, not the diagnostic entity.
    // Diagnostics are independent pages managed via the page manager.
  }

  // Regular block deletion for standard blocks or fallback
  console.log(`Using regular block deletion for block ID: ${blockId}`);
  return apiDelete(getBlockApiUrl(blockId));
}

export function checkAuthForBlockOperations() {
  if (!isAuthenticated()) {
    showErrorNotification('Authentication required to modify blocks');
    return false;
  }
  return true;
}

export async function verifyAuthForBlockOperations() {
  if (!isAuthenticated()) {
    showErrorNotification('Authentication required to modify blocks');
    return false;
  }

  try {
    await verifyAuth(getAuthToken());
    return true;
  } catch (error) {
    console.error('[BlockSystem] Authorization error:', error);
    showErrorNotification('Authentication error');
    return false;
  }
}